/**
 * Step-up da suíte (SPEC-v2 2.5): `const confirm = useStepUp(); if (await confirm(motivo)) …`.
 * Se a confirmação dos últimos 15 min ainda vale, resolve na hora; senão abre o `StepUpSheet`.
 */
import { createContext, type ReactNode, Suspense, useCallback, useContext, useMemo, useState } from 'react';
import { sessionStore, stepUpFresh, useSession } from '@/data/auth/session';
import { getSuite } from '@/data/provider';
import { lazyPart } from '@/lib/lazyPart';

type Confirm = (reason: string) => Promise<boolean>;

const StepUpContext = createContext<Confirm>(async () => false);

const sheet = lazyPart(() => import('@/components/suite/StepUpSheet').then((m) => m.StepUpSheet));

export function StepUpProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [pending, setPending] = useState<{ reason: string; resolve(ok: boolean): void } | null>(null);
  const [seen, setSeen] = useState(false);

  const confirm = useCallback<Confirm>((reason) => {
    if (stepUpFresh(sessionStore.get().session)) return Promise.resolve(true);
    setSeen(true);
    return new Promise<boolean>((resolve) => setPending({ reason, resolve }));
  }, []);

  const close = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  const value = useMemo(() => confirm, [confirm]);
  return (
    <StepUpContext.Provider value={value}>
      {children}
      {seen && (
        <Suspense fallback={null}>
          <sheet.Part
            open={pending != null}
            reason={pending?.reason ?? ''}
            totpAvailable={session?.mfa.enrolled ?? false}
            verify={async (i: { password?: string; totp?: string }) => {
              const s = await getSuite().auth.stepUp(i);
              sessionStore.signedIn(s);
            }}
            onDone={() => close(true)}
            onCancel={() => close(false)}
          />
        </Suspense>
      )}
    </StepUpContext.Provider>
  );
}

export function useStepUp(): Confirm {
  return useContext(StepUpContext);
}
